/**
 * The flow canvas demo's model: one example flow, laid out on a canvas, and a
 * walker that takes an example person through it the way the engine would.
 *
 * ## THE GRAPH IS THE PRODUCT'S SHAPE
 *
 * Node types, their ports and their port ids are the product's
 * (`packages/shared/src/contracts/flow.ts`): a WhatsApp message with
 * quick-reply buttons has one path per button (`btn0`, `btn1`) and a
 * `noreply` path; a random split has one path per row plus `holdout`, which
 * leads nowhere — the held-out person gets nothing and is counted for lift; a
 * condition has `yes` and `no`; goal and exit end the journey. Labels are the
 * product's `FLOW_NODE_TYPE_LABEL`. The suite checks the port ids against
 * `portsOf` in the real contract.
 *
 * ## THE WALK IS SCRIPTED BY RULES, NOT BY HAND
 *
 * `walk()` follows the edges from the start node, asking the example person
 * only what a person decides — which split row the hash put them in, which
 * button they tap, whether they order — and applies the engine's own rules for
 * everything else: a wait until the next 10:00; quiet hours 21:00–09:00 IST;
 * the frequency cap counting every message, campaign and flow alike, in the
 * last seven days and deferring a send until the oldest leaves the window; a
 * goal that ends the journey as converted the moment an outcome lands. So the
 * cap deferral in Priya's walk is computed, not typed. Times are minutes from
 * a fixed start (Fri 9 Oct 2026 00:00 IST) so every reader sees the same walk.
 *
 * ## IT IS AN EXAMPLE
 *
 * The flow, the template wording, the people and the welcome message they
 * already had on Monday are all made up, and the section's first line says
 * "made-up people" and that Simulate sends nothing. Pressing Simulate sends nothing and calls nothing: the
 * product's own Test button is the same idea run against a real person in a
 * real workspace, and it sends nothing either.
 */

export type FlowNodeType = 'trigger' | 'message' | 'wait' | 'condition' | 'split' | 'update' | 'goal' | 'exit';

/** The product's `FLOW_NODE_TYPE_LABEL`, for the types this flow uses. */
export const NODE_TYPE_LABEL: Readonly<Record<FlowNodeType, string>> = {
  trigger: 'Start',
  message: 'Message',
  wait: 'Wait',
  condition: 'Condition',
  split: 'Random split',
  update: 'Profile update',
  goal: 'Goal',
  exit: 'Exit',
};

export interface FlowPort {
  readonly id: string;
  readonly label: string;
  /** Vertical offset of the port from the node's top, in canvas px. */
  readonly dy: number;
  /** A holdout port: no edge, a lock instead of a dot. */
  readonly holdout?: boolean;
}

export interface DemoFlowNode {
  readonly id: string;
  readonly type: FlowNodeType;
  readonly title: string;
  readonly x: number;
  readonly y: number;
  /** Card width; round logic nodes are always ROUND_W wide. */
  readonly w: number;
  readonly h: number;
  readonly channel?: 'whatsapp' | 'email';
  readonly ports: readonly FlowPort[];
  /** What the drawer shows: label → value, in order. */
  readonly config: readonly (readonly [string, string])[];
  /** Body text for a message bubble or an email. */
  readonly body?: string;
  readonly subject?: string;
  readonly buttons?: readonly string[];
  /** The line under a round node's circle, or a card's one-line summary. */
  readonly summary: string;
}

export interface DemoFlowEdge {
  readonly id: string;
  readonly from: string;
  readonly port: string;
  readonly to: string;
}

export const ROUND_W = 160;
export const CANVAS = { w: 2000, h: 660 } as const;

const ROW = 41;
const waRows = (buttons: readonly string[]): FlowPort[] => [
  ...buttons.map((label, index) => ({ id: `btn${index}`, label, dy: 146 + ROW * index + 17 })),
  { id: 'noreply', label: 'No reply in 48 hours', dy: 146 + ROW * buttons.length + 17 },
];
const waHeight = (buttons: readonly string[]): number => 146 + ROW * (buttons.length + 1) + 8;
const roundRows = (rows: readonly { id: string; label: string; holdout?: boolean }[]): FlowPort[] =>
  rows.map((row, index) => ({ ...row, dy: 88 + 18 + 36 * index }));

const REMINDER_BUTTONS = ['Show me', 'Not now'] as const;

export const FLOW_NAME = 'Lead nurture · Diwali';
export const FLOW_SEGMENT = 'New leads · Diwali ads';

export const FLOW_NODES: readonly DemoFlowNode[] = [
  {
    id: 'start',
    type: 'trigger',
    title: 'Start',
    x: 40,
    y: 220,
    w: 250,
    h: 132,
    ports: [{ id: 'out', label: 'Next', dy: 66 }],
    summary: `Enters “${FLOW_SEGMENT}”`,
    config: [
      ['Starts when', `a person enters “${FLOW_SEGMENT}”`],
      ['Re-entry', 'never'],
      ['Quiet hours', '21:00–09:00 IST'],
      ['Frequency cap', '2 messages per 7 days, campaigns included'],
    ],
  },
  {
    id: 'split',
    type: 'split',
    title: 'Random split',
    x: 340,
    y: 206,
    w: ROUND_W,
    h: 170,
    ports: roundRows([
      { id: 'treated', label: 'Treated 95%' },
      { id: 'holdout', label: 'Holdout 5%', holdout: true },
    ]),
    summary: 'Treated or held out',
    config: [
      ['Treated', '95%'],
      ['Holdout', '5%: no messages, counted for lift'],
    ],
  },
  {
    id: 'wait',
    type: 'wait',
    title: 'Wait',
    x: 560,
    y: 188,
    w: ROUND_W,
    h: 130,
    ports: [{ id: 'out', label: 'Next', dy: 105 }],
    summary: 'Until the next 10:00',
    config: [['Wait', 'until the next 10:00 IST']],
  },
  {
    id: 'reminder',
    type: 'message',
    channel: 'whatsapp',
    title: 'WhatsApp',
    x: 790,
    y: 150,
    w: 290,
    h: waHeight(REMINDER_BUTTONS),
    ports: waRows(REMINDER_BUTTONS),
    body: 'Still thinking about the kurta set, {{name}}? M and L are back at FC Road this week.',
    buttons: REMINDER_BUTTONS,
    summary: 'Reminder with two quick replies',
    config: [
      ['Template', 'diwali_reminder · approved'],
      ['No reply after', '48 hours'],
      ['Sent through', 'Connect, same checks'],
    ],
  },
  {
    id: 'offer',
    type: 'message',
    channel: 'whatsapp',
    title: 'WhatsApp',
    x: 1180,
    y: 30,
    w: 290,
    h: 160,
    ports: [{ id: 'out', label: 'Next', dy: 120 }],
    body: 'Here’s 10% off for Diwali: DIWALI10, valid till 20 Oct, online or in store.',
    summary: 'The offer',
    config: [
      ['Template', 'diwali_offer · approved'],
      ['Sent through', 'Connect, once the cap allows'],
    ],
  },
  {
    id: 'tag',
    type: 'update',
    title: 'Profile update',
    x: 1180,
    y: 300,
    w: 230,
    h: 100,
    ports: [{ id: 'out', label: 'Next', dy: 50 }],
    summary: 'Add tag “not-now”',
    config: [['Action', 'add the tag “not-now”']],
  },
  {
    id: 'has-email',
    type: 'condition',
    title: 'Condition',
    x: 1200,
    y: 450,
    w: ROUND_W,
    h: 170,
    ports: roundRows([
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
    ]),
    summary: 'Has an email address?',
    config: [['Yes when', 'Email is known']],
  },
  {
    id: 'email',
    type: 'message',
    channel: 'email',
    title: 'Email',
    x: 1440,
    y: 470,
    w: 270,
    h: 140,
    ports: [{ id: 'out', label: 'Next', dy: 70 }],
    subject: 'Your Diwali 10% is waiting',
    body: 'DIWALI10 takes 10% off the kurta set, online or at FC Road, till 20 Oct.',
    summary: 'The offer, by email',
    config: [
      ['Template', 'diwali_offer_email'],
      ['Sent through', 'your own Amazon SES account'],
    ],
  },
  {
    id: 'exit',
    type: 'exit',
    title: 'Exit',
    x: 1490,
    y: 300,
    w: 220,
    h: 100,
    ports: [],
    summary: 'Ends the journey',
    config: [['Cool-off', '30 days']],
  },
  {
    id: 'goal',
    type: 'goal',
    title: 'Goal',
    x: 1760,
    y: 200,
    w: 210,
    h: 100,
    ports: [],
    summary: 'An order within 14 days',
    config: [
      ['Reached when', 'an order within 14 days'],
      ['Ends', 'the journey, wherever the person is'],
    ],
  },
];

export const FLOW_EDGES: readonly DemoFlowEdge[] = [
  { id: 'e1', from: 'start', port: 'out', to: 'split' },
  { id: 'e2', from: 'split', port: 'treated', to: 'wait' },
  { id: 'e3', from: 'wait', port: 'out', to: 'reminder' },
  { id: 'e4', from: 'reminder', port: 'btn0', to: 'offer' },
  { id: 'e5', from: 'reminder', port: 'btn1', to: 'tag' },
  { id: 'e6', from: 'reminder', port: 'noreply', to: 'has-email' },
  { id: 'e7', from: 'offer', port: 'out', to: 'goal' },
  { id: 'e8', from: 'tag', port: 'out', to: 'exit' },
  { id: 'e9', from: 'has-email', port: 'yes', to: 'email' },
  { id: 'e10', from: 'has-email', port: 'no', to: 'exit' },
  { id: 'e11', from: 'email', port: 'out', to: 'goal' },
];

export const isRound = (type: FlowNodeType): boolean => type === 'split' || type === 'wait' || type === 'condition';

export const nodeById = (id: string): DemoFlowNode | undefined => FLOW_NODES.find((node) => node.id === id);

/** Where an edge leaves: the right edge of a card, or the right edge of a round node's rows. */
export function portPoint(node: DemoFlowNode, portId: string): { x: number; y: number } | null {
  const port = node.ports.find((candidate) => candidate.id === portId);
  if (port === undefined) return null;
  return { x: node.x + (isRound(node.type) ? 144 : node.w), y: node.y + port.dy };
}

/** Where an edge arrives: a card's left edge near the top, or a round node's circle. */
export function inPoint(node: DemoFlowNode): { x: number; y: number } {
  return isRound(node.type) ? { x: node.x + 53, y: node.y + 27 } : { x: node.x, y: node.y + 24 };
}

/** The concept's connector: leaves a port horizontally, arrives horizontally. */
export function curve(a: { x: number; y: number }, b: { x: number; y: number }): string {
  const x1 = a.x + 6;
  const x2 = b.x - 3;
  const dx = Math.max(46, Math.abs(x2 - x1) * 0.5);
  return `M ${x1} ${a.y} C ${x1 + dx} ${a.y}, ${x2 - dx} ${b.y}, ${x2} ${b.y}`;
}

/* -------------------------------------------------------------------------- */
/* Time                                                                        */
/* -------------------------------------------------------------------------- */

const DAY = 24 * 60;
/** Minute 0 is Fri 9 Oct 2026, 00:00 IST. */
const BASE_DAY_OF_MONTH = 9;
const WEEKDAYS = ['Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu'];

export function formatAt(minute: number): string {
  const day = Math.floor(minute / DAY);
  const inDay = minute - day * DAY;
  const hh = String(Math.floor(inDay / 60)).padStart(2, '0');
  const mm = String(inDay % 60).padStart(2, '0');
  return `${WEEKDAYS[day % 7]} ${BASE_DAY_OF_MONTH + day} Oct · ${hh}:${mm}`;
}

const at = (day: number, hh: number, mm: number): number => day * DAY + hh * 60 + mm;

const QUIET_START = 21 * 60;
const QUIET_END = 9 * 60;
const CAP = { max: 2, perDays: 7 } as const;

/** The first allowed minute at or after `minute`, outside 21:00–09:00. */
export function afterQuietHours(minute: number): number {
  const inDay = ((minute % DAY) + DAY) % DAY;
  if (inDay >= QUIET_START) return minute - inDay + DAY + QUIET_END;
  if (inDay < QUIET_END) return minute - inDay + QUIET_END;
  return minute;
}

/** The first minute at or after `minute` when fewer than CAP.max of `sent` fall in the last CAP.perDays days. */
export function afterFrequencyCap(minute: number, sent: readonly number[]): number {
  let candidate = minute;
  for (let guard = 0; guard < 10; guard += 1) {
    const window = sent.filter((time) => time > candidate - CAP.perDays * DAY && time <= candidate).sort((a, b) => a - b);
    if (window.length < CAP.max) return candidate;
    // Wait until the oldest message in the window leaves it, plus a minute.
    candidate = (window[0] ?? candidate) + CAP.perDays * DAY + 1;
  }
  return candidate;
}

/* -------------------------------------------------------------------------- */
/* Example people and the walk                                                 */
/* -------------------------------------------------------------------------- */

export interface SamplePerson {
  readonly id: string;
  readonly name: string;
  readonly blurb: string;
  readonly bucket: 'treated' | 'holdout';
  /** Which quick reply they tap, and how many minutes after the message; or no reply. */
  readonly reply: { readonly port: 'btn0' | 'btn1'; readonly after: number } | null;
  readonly hasEmail: boolean;
  /** Minutes after the last message they were sent when they order, or null. */
  readonly ordersAfter: number | null;
}

/** Messages each example person was already sent before the flow — Monday's welcome campaign. */
const WELCOME_AT = at(-4, 9, 30);

export const SAMPLE_PEOPLE: readonly SamplePerson[] = [
  { id: 'priya', name: 'Priya', blurb: 'taps “Show me”, then orders', bucket: 'treated', reply: { port: 'btn0', after: 7 }, hasEmail: false, ordersAfter: 369 },
  { id: 'rahul', name: 'Rahul', blurb: 'never replies, has an email', bucket: 'treated', reply: null, hasEmail: true, ordersAfter: null },
  { id: 'karan', name: 'Karan', blurb: 'taps “Not now”', bucket: 'treated', reply: { port: 'btn1', after: 20 }, hasEmail: true, ordersAfter: null },
  { id: 'meera', name: 'Meera', blurb: 'lands in the holdout', bucket: 'holdout', reply: null, hasEmail: true, ordersAfter: null },
];

export interface WalkStep {
  readonly nodeId: string;
  /** The path taken out of this node, or null when the journey ends here. */
  readonly port: string | null;
  readonly edgeId: string | null;
  readonly at: string;
  readonly line: string;
}

const edgeFrom = (nodeId: string, port: string): DemoFlowEdge | undefined =>
  FLOW_EDGES.find((edge) => edge.from === nodeId && edge.port === port);

const portLabel = (nodeId: string, port: string): string => nodeById(nodeId)?.ports.find((candidate) => candidate.id === port)?.label ?? port;

/** Walk one example person through the flow from launch (Fri 9 Oct 18:02). */
export function walk(person: SamplePerson): readonly WalkStep[] {
  const steps: WalkStep[] = [];
  const sent: number[] = [WELCOME_AT];
  let now = at(0, 18, 2);
  let nodeId: string | null = 'start';
  let lastSentAt = now;

  const push = (id: string, port: string | null, line: string, when = now): void => {
    const edge = port === null ? undefined : edgeFrom(id, port);
    steps.push({ nodeId: id, port, edgeId: edge?.id ?? null, at: formatAt(when), line });
  };

  const send = (id: string, what: string): void => {
    const quiet = afterQuietHours(now);
    // A time the cap allows may fall in quiet hours, and the reverse; settle both.
    let allowed = afterFrequencyCap(quiet, sent);
    for (let settle = 0; settle < 5 && afterQuietHours(allowed) !== allowed; settle += 1) {
      allowed = afterFrequencyCap(afterQuietHours(allowed), sent);
    }
    if (allowed > now) {
      const why = allowed > quiet ? `the frequency cap is full (${CAP.max} messages in ${CAP.perDays} days)` : 'it is inside quiet hours';
      steps.push({ nodeId: id, port: null, edgeId: null, at: formatAt(now), line: `Due now, but ${why}. Held until ${formatAt(allowed)}.` });
      now = allowed;
    }
    sent.push(now);
    lastSentAt = now;
    steps.push({ nodeId: id, port: null, edgeId: null, at: formatAt(now), line: `${what}: sent through Connect, delivered.` });
  };

  for (let guard = 0; guard < 20 && nodeId !== null; guard += 1) {
    const node = nodeById(nodeId);
    if (node === undefined) break;
    let port: string | null = null;

    switch (node.id) {
      case 'start':
        port = 'out';
        push(node.id, port, `${person.name} joins at launch, already in “${FLOW_SEGMENT}”.`);
        break;
      case 'split':
        if (person.bucket === 'holdout') {
          push(node.id, 'holdout', 'Held out (5%). Gets no messages from this flow, and is counted when Sense measures its lift.');
          return steps;
        }
        port = 'treated';
        push(node.id, port, 'Treated (95%). Continues.');
        break;
      case 'wait': {
        const tomorrow = Math.floor(now / DAY) * DAY + DAY + 10 * 60;
        push(node.id, null, 'Waits until the next 10:00.');
        now = tomorrow;
        port = 'out';
        steps.push({ nodeId: node.id, port, edgeId: edgeFrom(node.id, port)?.id ?? null, at: formatAt(now), line: 'Wait over.' });
        break;
      }
      case 'reminder':
        send(node.id, 'Reminder on WhatsApp');
        if (person.reply === null) {
          now += 48 * 60;
          port = 'noreply';
          push(node.id, port, 'No reply in 48 hours, so it takes the “No reply” path.');
        } else {
          now += person.reply.after;
          port = person.reply.port;
          push(node.id, port, `Taps “${portLabel(node.id, port)}”. That button is its own path.`);
        }
        break;
      case 'offer':
        send(node.id, 'Offer on WhatsApp');
        port = 'out';
        push(node.id, port, 'On to the goal.');
        break;
      case 'tag':
        port = 'out';
        push(node.id, port, 'Tag “not-now” added to the profile.');
        break;
      case 'has-email':
        port = person.hasEmail ? 'yes' : 'no';
        push(node.id, port, person.hasEmail ? 'Email is known: yes.' : 'No email address: no.');
        break;
      case 'email':
        send(node.id, 'Offer by email');
        port = 'out';
        push(node.id, port, 'On to the goal.');
        break;
      case 'exit':
        push(node.id, null, 'Exits. Will not start this flow again for 30 days.');
        return steps;
      case 'goal':
        if (person.ordersAfter !== null) {
          push(node.id, null, 'An order is recorded for this person, so the goal is reached. Converted; no more messages.', lastSentAt + person.ordersAfter);
        } else {
          push(node.id, null, 'No order within 14 days, so the journey ends without converting.', lastSentAt + 14 * DAY);
        }
        return steps;
    }

    nodeId = port === null ? null : (edgeFrom(node.id, port)?.to ?? null);
  }
  return steps;
}

/* -------------------------------------------------------------------------- */
/* Keyboard panning                                                            */
/* -------------------------------------------------------------------------- */

export type CanvasKeyAction = { readonly kind: 'pan'; readonly dx: number; readonly dy: number } | { readonly kind: 'zoom'; readonly by: number } | { readonly kind: 'fit' };

/** What a key does on the focused canvas; null for keys it leaves alone. Pure, so the suite tests it. */
export function canvasKeyAction(key: string): CanvasKeyAction | null {
  const STEP = 60;
  switch (key) {
    case 'ArrowLeft': return { kind: 'pan', dx: STEP, dy: 0 };
    case 'ArrowRight': return { kind: 'pan', dx: -STEP, dy: 0 };
    case 'ArrowUp': return { kind: 'pan', dx: 0, dy: STEP };
    case 'ArrowDown': return { kind: 'pan', dx: 0, dy: -STEP };
    case '+':
    case '=': return { kind: 'zoom', by: 1.15 };
    case '-':
    case '_': return { kind: 'zoom', by: 1 / 1.15 };
    case '0': return { kind: 'fit' };
    default: return null;
  }
}

export const clampScale = (scale: number): number => Math.min(1.4, Math.max(0.35, scale));
